// src/pages/ValidatedReportsPage.js

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../api/axiosConfig';
import './ManagementPages.css';

function ValidatedReportsPage() {
  const [visites, setVisites] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');
  const [zoneFilter, setZoneFilter] = useState('');
  const [merchandiserFilter, setMerchandiserFilter] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchValidatedVisites = async () => {
      try {
        const response = await axiosInstance.get('/admin/visites/validees');
        setVisites(response.data);
      } catch (error) {
        console.error("Erreur lors du chargement des rapports validés :", error);
        alert("Impossible de charger les rapports.");
      } finally {
        setIsLoading(false);
      }
    };
    fetchValidatedVisites();
  }, []);

  const zones = useMemo(() => {
    const set = new Set(visites.map(v => v.client?.zone || v.merchandiser?.chef_zone?.zone).filter(Boolean));
    return [...set].sort();
  }, [visites]);

  const merchandisers = useMemo(() => {
    const set = new Set(visites.map(v => v.merchandiser?.user?.nom).filter(Boolean));
    return [...set].sort();
  }, [visites]);

  const visitesFiltrees = useMemo(() => {
    return visites.filter(v => {
      const dateVisite = new Date(v.date_visite);
      if (dateDebut && dateVisite < new Date(dateDebut)) return false;
      if (dateFin && dateVisite > new Date(dateFin + 'T23:59:59')) return false;
      const zone = v.client?.zone || v.merchandiser?.chef_zone?.zone || '';
      if (zoneFilter && zone !== zoneFilter) return false;
      const merch = v.merchandiser?.user?.nom || '';
      if (merchandiserFilter && merch !== merchandiserFilter) return false;
      const client = v.client?.nom_client || '';
      if (clientFilter && !client.toLowerCase().includes(clientFilter.toLowerCase())) return false;
      return true;
    });
  }, [visites, dateDebut, dateFin, zoneFilter, merchandiserFilter, clientFilter]);

  const resetFiltres = () => {
    setDateDebut('');
    setDateFin('');
    setZoneFilter('');
    setMerchandiserFilter('');
    setClientFilter('');
  };

  if (isLoading) {
    return <div>Chargement des rapports validés...</div>;
  }

  return (
    <div className="management-page">
      <h1>Historique des Rapports Validés</h1>

      <div style={{ background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '8px', padding: '16px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-end' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '12px', fontWeight: '600', color: '#495057' }}>Date début</label>
            <input type="date" value={dateDebut} onChange={e => setDateDebut(e.target.value)}
              style={{ padding: '7px 10px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '14px' }} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '12px', fontWeight: '600', color: '#495057' }}>Date fin</label>
            <input type="date" value={dateFin} onChange={e => setDateFin(e.target.value)}
              style={{ padding: '7px 10px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '14px' }} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '12px', fontWeight: '600', color: '#495057' }}>Zone</label>
            <select value={zoneFilter} onChange={e => setZoneFilter(e.target.value)}
              style={{ padding: '7px 10px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '14px', minWidth: '150px' }}>
              <option value="">Toutes les zones</option>
              {zones.map(z => <option key={z} value={z}>{z}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '12px', fontWeight: '600', color: '#495057' }}>Merchandiser</label>
            <select value={merchandiserFilter} onChange={e => setMerchandiserFilter(e.target.value)}
              style={{ padding: '7px 10px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '14px', minWidth: '180px' }}>
              <option value="">Tous les merchandisers</option>
              {merchandisers.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '12px', fontWeight: '600', color: '#495057' }}>Client</label>
            <input type="text" placeholder="Rechercher un client..." value={clientFilter} onChange={e => setClientFilter(e.target.value)}
              style={{ padding: '7px 10px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '14px', minWidth: '180px' }} />
          </div>
          <button onClick={resetFiltres}
            style={{ padding: '7px 16px', background: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' }}>
            Réinitialiser
          </button>
        </div>
        <div style={{ marginTop: '8px', fontSize: '13px', color: '#6c757d' }}>
          {visitesFiltrees.length} rapport(s) affiché(s) sur {visites.length}
        </div>
      </div>

      <div className="management-table-container">
        {visitesFiltrees.length > 0 ? (
          <table className="data-table">
            <thead>
              <tr>
                <th>Date Visite</th>
                <th>Zone</th>
                <th>Merchandiser</th>
                <th>Client</th>
                <th>Statut</th>
                <th>Validé par</th>
                <th>Date Validation</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {visitesFiltrees.map(visite => (
                <tr key={visite.id}>
                  <td>{new Date(visite.date_visite).toLocaleDateString()}</td>
                  <td>{visite.client?.zone || visite.merchandiser?.chef_zone?.zone || 'N/A'}</td>
                  <td>{visite.merchandiser?.user?.nom || 'N/A'}</td>
                  <td>{visite.client?.nom_client || 'N/A'}</td>
                  <td>
                    <span className="status-badge approved">{visite.statut_validation}</span>
                  </td>
                  <td>{visite.validateur?.nom || 'N/A'}</td>
                  <td>{visite.date_validation ? new Date(visite.date_validation).toLocaleDateString() : 'N/A'}</td>
                  <td>
                    <button onClick={() => navigate(`/visite/${visite.id}`)} className="action-button view-button">
                      Voir Détails
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p>Aucun rapport trouvé avec ces filtres.</p>
        )}
      </div>
    </div>
  );
}

export default ValidatedReportsPage;